import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Select } from "antd";
import { searchFaxMatters } from "../../../service/fax";

const SEARCH_DELAY_MS = 300;

const FaxMatterSelect = ({
  value,
  onChange,
  disabled = false,
  currentMatterId = "",
  currentMatterName = "",
  dataTestId,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  const currentMatterOption = useMemo(
    () =>
      currentMatterId
        ? [
            {
              value: String(currentMatterId),
              label: currentMatterName || "Current matter",
            },
          ]
        : [],
    [currentMatterId, currentMatterName],
  );

  // The retained label is a cache of the last in-component pick; a parent
  // clearing or replacing the controlled value (e.g. after a send) must
  // invalidate it, or the Select keeps showing a matter that is no longer
  // part of the next request.
  useEffect(() => {
    setSelectedOption((previous) =>
      previous && previous.value === String(value || "") ? previous : null,
    );
  }, [value]);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await searchFaxMatters(term);
        if (!cancelled) {
          setResults(
            (response?.data?.results || []).map((matter) => ({
              value: String(matter.id),
              label: matter.display_name || String(matter.id),
            })),
          );
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, SEARCH_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const options = [
    ...currentMatterOption,
    ...(selectedOption &&
    selectedOption.value !== String(currentMatterId || "")
      ? [selectedOption]
      : []),
    ...results.filter(
      (result) =>
        result.value !== String(currentMatterId || "") &&
        result.value !== selectedOption?.value,
    ),
  ];

  const handleChange = (matterId, option) => {
    const selectedId = matterId ? String(matterId) : "";
    setSelectedOption(
      selectedId
        ? {
          value: selectedId,
          label: option?.label || selectedId,
        }
        : null,
    );
    onChange(selectedId);
  };

  return (
    <Select
      allowClear
      showSearch
      style={{ width: "100%" }}
      value={value || undefined}
      disabled={disabled}
      filterOption={false}
      placeholder="Search matter or client name"
      notFoundContent={searching ? "Searching…" : null}
      options={options}
      onSearch={setQuery}
      onChange={handleChange}
      data-testid={dataTestId}
    />
  );
};

FaxMatterSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  currentMatterId: PropTypes.string,
  currentMatterName: PropTypes.string,
  dataTestId: PropTypes.string,
};

export default FaxMatterSelect;
